/**
 * Created by Ning Jiang on 2/7/2018.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {IWinRatioConfig} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Power4, Timeline, TimelineLite, TweenLite} from "gsap";
import {TombOfNefertitiGameConfig} from "../../TombOfNefertitiGameConfig";
import {CoinsFountain} from "./CoinsFountain";

export class TombOfNefertitiBigWinView extends BaseView {

    private _isShowing:boolean = false;
    private _dimmer:PIXI.Graphics;

    private _layer:ScalableLayer;
    private _shineContainer:PIXI.Container;
    private _wingsContainer:PIXI.Container;
    private _shines:TimelineSprite[] = [];
    private _wings:TimelineSprite[] = [];

    private _positionOffsets:number[][] = [[10, 12], [9, 14], [13, 18]];

    private _coinsFountain:CoinsFountain;

    constructor() {
        super();

        this._layer = StageManager.getLayer(TombOfNefertitiGameConfig.instance.LAYERS.BIG_WIN.name);
        this._coinsFountain = new CoinsFountain();
    }

    public getBigWinLevelAnimation(winRatioConfig:IWinRatioConfig, level:number):Timeline {
        this._isShowing = true;
        if(this._isResizeDirty) {
            this.onResize(this._resizeData);
        }

        const timeline:TimelineLite = new TimelineLite(),
            duration:number = 0.5;

        timeline.add([
            () => {
                this._coinsFountain.startFountain(level);
            },
            TweenLite.to(this._dimmer, 0.1, {alpha : 1}),
            TweenLite.to(this._wings[level], duration, {alpha : 1}),
            TweenLite.fromTo(this._wings[level].scale, duration, {x : 2, y : 2}, {x : 1, y : 1, ease : Power4.easeIn}),
            TweenLite.to(() => {}, winRatioConfig.duration, {}) // this tween has to be added to increase the big win count up duration(similar tween is applied in other games)
        ]);

        timeline.add(() => this._wings[level].getAnimation(), duration);
        timeline.add(() => {
            this._shines[level].alpha = 1;
            this._shines[level].playLoop();
        }, duration);

        if(level > 0) {
            timeline.add([
                TweenLite.to(this._wings[level - 1], 0.4, {alpha : 0}),
                TweenLite.to(this._shines[level - 1], 0.4, {alpha : 0}),
            ], 0);
        }

        return timeline;
    }

    public playEndingAnimation(endLevel:number):Timeline {
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this._wings[endLevel].scale.set(1);
                this._coinsFountain.stopFountain();
            },
            TweenLite.to(this._dimmer, 0.2, {alpha : 0, delay : 0.3}),
            TweenLite.to(this._wings[endLevel], 0.2, {alpha : 0, delay : 0.3}),
            TweenLite.to(this._shines[endLevel], 0.2, {alpha : 0, delay : 0.3})
        ]);

        if(endLevel > 0) {
            for(let i:number = 0; i < endLevel; i++) {
                timeline.add(TweenLite.to(this._wings[i], 0.3, {alpha : 0}), 0);
                timeline.add(TweenLite.to(this._shines[i], 0.3, {alpha : 0}), 0);
            }
        }

        timeline.add(() => this._isShowing = false);

        return timeline;
    }

    protected initAnimations():void {
        const bigWinLevels:number = 3;

        this._dimmer = new PIXI.Graphics();
        this._dimmer.alpha = 0;
        StageManager.getLayer(TombOfNefertitiGameConfig.instance.LAYERS.FOUNTAIN.name).addChildAt(this._dimmer, 0);

        this._shineContainer = new PIXI.Container();
        this._shineContainer.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.27);
        this._layer.addChild(this._shineContainer);

        this._wingsContainer = new PIXI.Container();
        this._wingsContainer.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.27);
        this._layer.addChild(this._wingsContainer);

        for(let i:number = 0; i < bigWinLevels; i++) {
            const shine:TimelineSprite = new TimelineSprite(GameResources.getTextures(`bigWinText${i}`));
            shine.blendMode = PIXI.BLEND_MODES.ADD;
            shine.anchor.set(0.5);
            shine.alpha = 0;
            shine.position.set(this._positionOffsets[i][0], this._positionOffsets[i][1]);
            this._shineContainer.addChild(shine);
            this._shines.push(shine);

            const wings:TimelineSprite = new TimelineSprite(GameResources.getTextures(`bigWinText${i}Wings`));
            wings.anchor.set(0.5);
            wings.alpha = 0;
            this._wingsContainer.addChild(wings);
            this._wings.push(wings);
        }
    }

    protected onResize(resizeData:IResizeData):void {
        if(!this._isShowing) {
            this._isResizeDirty = true;
            return;
        }

        this._isResizeDirty = false;

        const screenWidth:number = Math.ceil(resizeData.width / this._layer.scale.x);
        const screenHeight:number = Math.ceil(resizeData.height / this._layer.scale.y);
        const leftOffset:number = Math.floor(-this._layer.x / this._layer.scale.x);
        const topOffset:number = Math.floor(-this._layer.y / this._layer.scale.y);

        this._dimmer.clear();
        this._dimmer.beginFill(0x000000, 0.8);
        this._dimmer.drawRect(leftOffset, topOffset, screenWidth, screenHeight);
    }
}